import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, FolderKanban, CalendarRange, Users2, Sparkles, Target, FileBarChart2, DatabaseZap, Settings2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ImpactOverview from './ImpactOverview';
import ImpactPrograms from './ImpactPrograms';
import ImpactEvents from './ImpactEvents';
import ImpactPeople from './ImpactPeople';
import ImpactQuality from './ImpactQuality';
import ImpactOutcomes from './ImpactOutcomes';
import ImpactMetrics from './ImpactMetrics';
import ImpactReports from './ImpactReports';
import ImpactImports from './ImpactImports';

export const BRAND = { teal: '#079B97', navy: '#0A2037', lime: '#95C73E', gold: '#FBBD55' };

const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'programs', label: 'Programs & Initiatives', icon: FolderKanban },
  { key: 'events', label: 'Trainings & Events', icon: CalendarRange },
  { key: 'people', label: 'Participants & Organizations', icon: Users2 },
  { key: 'outcomes', label: 'Outcomes & Evidence', icon: Sparkles },
  { key: 'metrics', label: 'Metrics & Targets', icon: Target },
  { key: 'reports', label: 'Reports & Charts', icon: FileBarChart2 },
  { key: 'imports', label: 'Import & Export', icon: DatabaseZap },
  { key: 'quality', label: 'Data Quality & Settings', icon: Settings2 },
];

export const ImpactDashboardTab = ({ API_URL, getAuthHeader }) => {
  const [section, setSection] = useState('overview');
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const runSync = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const r = await fetch(`${API_URL}/api/impact/sync`, { method: 'POST', headers: getAuthHeader() });
      if (r.ok) {
        const data = await r.json();
        setLastSync(data.last_sync);
        setRefreshKey(k => k + 1);
        const errors = (data.results || []).filter(x => x.status === 'error');
        if (!silent) {
          if (errors.length) toast.error(`Sync finished with ${errors.length} source error(s)`);
          else toast.success('Records synced from all sources');
        }
      }
    } catch { if (!silent) toast.error('Sync failed'); }
    setSyncing(false);
  }, [API_URL, getAuthHeader]);

  useEffect(() => { runSync(true); }, [runSync]);

  const props = { API_URL, getAuthHeader, refreshKey };

  return (
    <div data-testid="impact-dashboard" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
      <div className="rounded-xl px-6 py-5 text-white mb-5 flex items-center justify-between flex-wrap gap-3" style={{ background: BRAND.navy }}>
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BRAND.gold }}>DCELC</p>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>Impact &amp; Engagement Dashboard</h2>
          <p className="text-xs mt-1 text-slate-300">
            Connected to live registrations, trainings, campaigns &amp; certificates.
            {lastSync && <span> Last synced {new Date(lastSync).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET</span>}
          </p>
        </div>
        <button
          onClick={() => runSync(false)}
          disabled={syncing}
          data-testid="impact-sync-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
          style={{ background: BRAND.teal, color: '#fff' }}
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing…' : 'Sync records'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const active = section === s.key;
          const disabled = Boolean(s.phase);
          return (
            <button
              key={s.key}
              onClick={() => !disabled && setSection(s.key)}
              disabled={disabled}
              data-testid={`impact-nav-${s.key}`}
              title={disabled ? `Coming in ${s.phase}` : undefined}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${disabled ? 'opacity-45 cursor-not-allowed bg-slate-100 text-slate-500' : ''}`}
              style={active ? { background: BRAND.teal, color: '#fff' } : !disabled ? { background: '#fff', color: BRAND.navy, border: '1px solid #e2e8f0' } : {}}
            >
              <Icon className="w-4 h-4" />
              {s.label}
              {s.phase && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">{s.phase}</span>}
            </button>
          );
        })}
      </div>

      {section === 'overview' && <ImpactOverview {...props} />}
      {section === 'programs' && <ImpactPrograms {...props} />}
      {section === 'events' && <ImpactEvents {...props} />}
      {section === 'people' && <ImpactPeople {...props} />}
      {section === 'outcomes' && <ImpactOutcomes {...props} />}
      {section === 'metrics' && <ImpactMetrics {...props} />}
      {section === 'reports' && <ImpactReports {...props} />}
      {section === 'imports' && <ImpactImports {...props} />}
      {section === 'quality' && <ImpactQuality {...props} />}
    </div>
  );
};

export default ImpactDashboardTab;
